import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/layout/Navbar';
import STLViewer from '../components/3d/STLViewer';
import Button from '../components/common/Button';
import './LandingPage.css';

function LandingPage() {
  const [viewerHeight, setViewerHeight] = useState(null);
  const step2ImageRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handleSignUpClick = useCallback(() => {
    navigate('/auth?mode=signup');
  }, [navigate]);

  const handleLogInClick = useCallback(() => {
    navigate('/auth?mode=login');
  }, [navigate]);

  const handleMyStudioClick = useCallback(() => {
    navigate('/home');
  }, [navigate]);

  // Keep Step 3 (3D viewer) height equal to Step 2 image height on mobile
  const updateHeights = useCallback(() => {
    const isMobile = window.innerWidth <= 900; // Match mobile breakpoint used in CSS
    if (!isMobile) {
      setViewerHeight(null);
      return;
    }

    const imgEl = step2ImageRef.current;
    const measured = imgEl?.offsetHeight || 0;
    if (measured > 0) {
      setViewerHeight(measured);
    }
  }, []);

  useEffect(() => {
    updateHeights();
    window.addEventListener('resize', updateHeights);
    window.addEventListener('orientationchange', updateHeights);

    const imgEl = step2ImageRef.current;
    if (imgEl) {
      if (imgEl.complete) {
        updateHeights();
      } else {
        imgEl.addEventListener('load', updateHeights);
      }
    }

    return () => {
      window.removeEventListener('resize', updateHeights);
      window.removeEventListener('orientationchange', updateHeights);
      if (imgEl) imgEl.removeEventListener('load', updateHeights);
    };
  }, [updateHeights]);

  return (
    <div className="App landing-page">
      <Navbar showAuthButton={false} />

      <div className="content">
        <section id="section1" className="full-height-section">
          <div className="section-content">
            <div className="hero-content">
              <div className="hero-text">
                <h1>
                  <span className="header-small">
                    create
                    <br />
                    your
                  </span>
                  <br />
                  <span className="header-large">tiniego</span>
                </h1>
                <p>
                  turn memories into something <br></br>small, fun, and personal — your tiniego
                </p>
                {isAuthenticated ? (
                  <Button variant="primary" className="auth-button" onClick={handleMyStudioClick}>
                    My Studio
                  </Button>
                ) : (
                  <div className="auth-buttons-container">
                    <Button variant="primary" className="auth-button" onClick={handleSignUpClick}>
                      Sign up
                    </Button>
                    <Button variant="secondary" className="auth-button" onClick={handleLogInClick}>
                      Log in
                    </Button>
                  </div>
                )}
              </div>
              <img 
                className="hero-image" 
                src="/landing_page.png" 
                alt="Example of a 3D printed tiniego figurine showing the final product"
                loading="eager"
              />
            </div>
          </div>
        </section>
        <section id="how-it-works" className="how-it-works-section">
          <div className="section-content">
            <h2>How it works</h2>
            <p>Turn your memories into a tiniego — in three simple steps.</p>

            <div className="steps-grid">
              <div className="step-card">
                <div className="step-media" aria-hidden="true">
                  <img
                    src="/landing_page_step_1.png"
                    alt="Step 1: Upload a photo to create your tiniego"
                    className="step-image"
                    loading="lazy"
                  />
                </div>
                <div className="step-meta">
                  <div className="step-number">1</div>
                  <h3>Pick your memory</h3>
                  <p>Snap or upload a photo — this is where your tiniego adventure begins!</p>
                </div>
              </div>

              <div className="step-card">
                <div className="step-media-auto-height" aria-hidden="true">
                  <img
                    src="/landing_page_step_2.png"
                    alt="Step 2: AI-generated tiniego design based on your photo"
                    className="step-image-auto-height"
                    ref={step2ImageRef}
                    loading="lazy"
                  />
                </div>
                <div className="step-meta">
                  <div className="step-number">2</div>
                  <h3>Meet your tiniego</h3>
                  <p>In minutes, we’ll work our magic and turn your memory into a tiniego.</p>
                </div>
              </div>

              <div className="step-card">
                <div className="step-media-auto-height" aria-hidden="true">
                  <STLViewer
                    stlUrl="/landing_page_step_3.stl"
                    width="100%"
                    height={viewerHeight || '100%'}
                    showRainbowBorder={false}
                    borderRadius={0}
                    showShadow={false}
                  />
                </div>
                <div className="step-meta">
                  <div className="step-number">3</div>
                  <h3>Ready, set, 3D!</h3>
                  <p>Finally, we will generate a 3D model of your tiniego ready for print.</p>
                </div>
              </div>
            </div>

            <div className="steps-conclusion">
              <div className="steps-conclusion-title">And that's it!</div>
              <p className="steps-conclusion-sub">
                Place your order and we’ll 3D print your tiniego in crisp white, pop it on a base,
                and deliver it straight to your door.
              </p>
            </div>

            <div className="how-cta">
              <div className="auth-buttons-container">
                <Button variant="primary" className="auth-button" onClick={handleSignUpClick}>
                  Sign up
                </Button>
                <Button variant="secondary" className="auth-button" onClick={handleLogInClick}>
                  Log in
                </Button>
              </div>
            </div>
          </div>
        </section>
        <section id="examples" className="examples-section">
          <div className="section-content">
            <h2>How it looks!</h2>
            <p className="examples-subtitle">
              This is how your tiniego will arrive in print! Whether you dress it up with paint or
              keep it simple as is, the choice is all yours.
            </p>
            <div className="examples-image-container">
              <img 
                src="/examples.png" 
                alt="Examples of 3D printed tiniego figurines in various designs and colors" 
                className="examples-image"
                loading="lazy"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default LandingPage;
